import { useCallback, useEffect, useRef, useState } from "react";
import { PAIRING_POLL_INTERVAL_MS, secondsUntilExpiry } from "../whatsapp-qr";
import type { PairingView } from "../whatsapp-qr";
import type { WhatsAppWizardApi } from "./api";

export type PairingPhase =
  | "loading"
  | "ready"
  | "expired"
  | "scanning"
  | "connected"
  | "logged_out"
  | "error";

export interface PairingHookState {
  readonly phase: PairingPhase;
  readonly view: PairingView | null;
  readonly secondsLeft: number | null;
  readonly refreshing: boolean;
  readonly codeSubmitting: boolean;
  refresh(): void;
  /** Starts (or restarts) a phone-number code flow with cleaned digits. */
  startCode(phoneNumber: string): void;
}

/**
 * Drives the in-page pairing step: it fetches the pairing view, polls the API
 * (which proxies the bridge) on an interval, keeps a one-second countdown for
 * the code expiry, and stops once the state is terminal. `onConnected` fires
 * exactly once. Every request is abort-scoped so an unmount cannot leak a
 * pending fetch or a state update.
 */
export function usePairing(
  api: WhatsAppWizardApi,
  connectionId: string | null,
  onConnected: () => void,
  pollIntervalMs: number = PAIRING_POLL_INTERVAL_MS,
): PairingHookState {
  const [view, setView] = useState<PairingView | null>(null);
  const [errored, setErrored] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [codeSubmitting, setCodeSubmitting] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const connectedRef = useRef(false);
  const onConnectedRef = useRef(onConnected);
  onConnectedRef.current = onConnected;

  const settle = useCallback((next: PairingView) => {
    setErrored(false);
    setView(next);
    if (next.state === "connected" && !connectedRef.current) {
      connectedRef.current = true;
      onConnectedRef.current();
    }
  }, []);

  useEffect(() => {
    if (!connectionId) return;
    connectedRef.current = false;
    let active = true;
    const controller = new AbortController();

    const load = async () => {
      try {
        const next = await api.getPairing(connectionId, controller.signal);
        if (active) settle(next);
      } catch {
        if (active) setErrored(true);
      }
    };

    void load();
    const poll = globalThis.setInterval(() => {
      if (connectedRef.current) return;
      void load();
    }, pollIntervalMs);
    const tick = globalThis.setInterval(() => {
      if (active) setNow(Date.now());
    }, 1_000);

    return () => {
      active = false;
      controller.abort();
      globalThis.clearInterval(poll);
      globalThis.clearInterval(tick);
    };
  }, [api, connectionId, pollIntervalMs, settle]);

  const refresh = useCallback(() => {
    if (!connectionId || refreshing) return;
    setRefreshing(true);
    setErrored(false);
    void api
      .refreshPairing(connectionId)
      .then((next) => settle(next))
      .catch(() => setErrored(true))
      .finally(() => setRefreshing(false));
  }, [api, connectionId, refreshing, settle]);

  const startCode = useCallback(
    (phoneNumber: string) => {
      if (!connectionId || codeSubmitting) return;
      setCodeSubmitting(true);
      setErrored(false);
      void api
        .startPairingCode(connectionId, phoneNumber)
        .then((next) => settle(next))
        .catch(() => setErrored(true))
        .finally(() => setCodeSubmitting(false));
    },
    [api, connectionId, codeSubmitting, settle],
  );

  const secondsLeft = view ? secondsUntilExpiry(view.expiresAt, now) : null;
  return {
    phase: derivePhase(view, errored, secondsLeft),
    view,
    secondsLeft,
    refreshing,
    codeSubmitting,
    refresh,
    startCode,
  };
}

function derivePhase(
  view: PairingView | null,
  errored: boolean,
  secondsLeft: number | null,
): PairingPhase {
  if (errored) return "error";
  if (!view) return "loading";
  if (view.state === "connected") return "connected";
  if (view.state === "logged_out") return "logged_out";
  if (view.state === "pairing") return "scanning";
  // awaiting_qr / qr_ready: a live, unexpired QR is ready to scan; a code with
  // no time left is expired; anything before a QR arrives is still loading.
  if (secondsLeft === 0) return "expired";
  if (view.qrDataUrl) return "ready";
  return "loading";
}
